import React, { useMemo } from 'react';
import {
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { SalesKpiTimeRange } from '@domain/sales/models/SalesKpis';
import { useSalesKpis } from '@features/sales/hooks/useSalesKpis';
import { useUIStateStore } from '../../../../application/store/UIStateStore';
import { APP_CONFIG } from '../../../../config/app.config';
import DashboardCardHeader from '../general/DashboardCardHeader';
import DashboardCardContent from '../general/DashboardCardContent';
import DashboardStatusChip from '../general/DashboardStatusChip';

const formatNumber = (value: number): string =>
  Math.round(value).toLocaleString('nl-NL');

const DEFAULT_SALES_KPI_RANGE = 'ytd';

type SalesKpiRangeOption = {
  value: string;
  label: string;
  range: SalesKpiTimeRange;
  weeks?: number;
  subtitle: string;
};

const SALES_KPI_RANGE_OPTIONS: SalesKpiRangeOption[] = [
  {
    value: 'this_week',
    label: 'This Week',
    range: SalesKpiTimeRange.THIS_WEEK,
    subtitle: 'Sold this week',
  },
  {
    value: 'last_week',
    label: 'Last Week',
    range: SalesKpiTimeRange.LAST_WEEK,
    subtitle: 'Sold last week',
  },
  {
    value: 'last_weeks_4',
    label: 'Last 4 weeks',
    range: SalesKpiTimeRange.LAST_WEEKS,
    weeks: 4,
    subtitle: 'Total over the last 4 weeks',
  },
  {
    value: 'last_weeks_12',
    label: 'Last 12 weeks',
    range: SalesKpiTimeRange.LAST_WEEKS,
    weeks: 12,
    subtitle: 'Total over the last 12 weeks',
  },
  {
    value: 'ytd',
    label: 'YTD',
    range: SalesKpiTimeRange.YTD,
    subtitle: 'Total sales year to date',
  },
];

const SalesKpiCard: React.FC = () => {
  const colors = APP_CONFIG.ui.theme.colors;

  const salesRangeOption = useUIStateStore(
    (state) =>
      state.filters.dashboard?.salesKpiRangeOption ?? DEFAULT_SALES_KPI_RANGE,
  );

  const setFilter = useUIStateStore((state) => state.setFilter);

  const selectedSalesRange = useMemo(
    () =>
      SALES_KPI_RANGE_OPTIONS.find((option) => option.value === salesRangeOption)
      ?? SALES_KPI_RANGE_OPTIONS.find((option) => option.value === DEFAULT_SALES_KPI_RANGE)!,
    [salesRangeOption],
  );

  const salesKpiOptions = useMemo(
    () => ({
      range: selectedSalesRange.range,
      weeks: selectedSalesRange.weeks,
    }),
    [selectedSalesRange],
  );

  const { salesKpis, isLoading } = useSalesKpis(salesKpiOptions);
  
  const changePercent = salesKpis?.changePercent ?? null;

  const handleSalesRangeChange = (event: SelectChangeEvent) => {
    setFilter('dashboard', 'salesKpiRangeOption', event.target.value);
  };

  return (
    <Stack minWidth={0} overflow="hidden" height="100%">
      <DashboardCardHeader icon="Bag01" title="Sales">
        <Select
          value={salesRangeOption}
          onChange={handleSalesRangeChange}
          size="small"
          variant="standard"
          disableUnderline
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: colors.secondaryText,
            '& .MuiSelect-select': {
              py: 0,
              pr: '24px !important',
            },
          }}
        >
          {SALES_KPI_RANGE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </DashboardCardHeader>
      <DashboardCardContent>
        {isLoading && !salesKpis ? (
          <Stack alignItems="center" justifyContent="center" height={58}>
            <CircularProgress size={20} />
          </Stack>
        ) : (
          <>
            <Stack direction="row" alignItems="center" gap={1} height={42}>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: 28,
                  lineHeight: '36px',
                  color: colors.primaryText,
                }}
              >
                {formatNumber(salesKpis?.totalQuantity ?? 0)}
              </Typography>
              {changePercent != null && selectedSalesRange.range !== SalesKpiTimeRange.THIS_WEEK && (
                <DashboardStatusChip
                  label={`${changePercent >= 0 ? '+' : ''}${Math.round(changePercent)}%  YoY`}
                  tone={changePercent >= 0 ? 'success' : 'error'}
                />
              )}
            </Stack>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: 10,
                lineHeight: '16px',
                letterSpacing: '0.5px',
                color: colors.secondaryText,
              }}
            >
              {selectedSalesRange.subtitle}
            </Typography>
          </>
        )}
      </DashboardCardContent>
    </Stack>
  );
};

export default SalesKpiCard;
